import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "../../styles/scroll-top.css";

const reducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

const scrollToY = (top, smooth) => {
  try {
    window.scrollTo({
      top,
      left: 0,
      behavior: smooth && !reducedMotion() ? "smooth" : "instant",
    }); // "instant" also beats a site-wide scroll-behavior: smooth
  } catch (e) {
    window.scrollTo(0, top);
  }
};

function scrollToHash(hash, smooth, offset) {
  const id = decodeURIComponent(hash.slice(1));
  const timers = [];
  let done = false;

  const events = ["wheel", "touchstart", "keydown", "mousedown"];

  const stop = () => {
    done = true;
    timers.forEach(clearTimeout);
    events.forEach((e) => window.removeEventListener(e, stop));
  };

  const targetTop = (el) =>
    el.getBoundingClientRect().top +
    window.scrollY -
    (parseFloat(getComputedStyle(el).scrollMarginTop) || offset);

  const settle = (el) => {
    if (!done && Math.abs(targetTop(el) - window.scrollY) > 6)
      scrollToY(targetTop(el), false);
  };
  let tries = 0;

  const attempt = () => {
    if (done) return;
    const el = document.getElementById(id);

    if (el) {
      scrollToY(targetTop(el), smooth);
      (smooth ? [1000] : [250, 900]).forEach((ms) =>
        timers.push(setTimeout(() => settle(el), ms)),
      );
    } else if (id === "top" || !id) {
      scrollToY(0, smooth);
    } else if (++tries < 40) {
      timers.push(setTimeout(attempt, 50));
    }
  };

  events.forEach((e) => window.addEventListener(e, stop, { passive: true }));

  attempt();

  return stop;
}

export default function ScrollToTop({
  threshold = 400,
  onlyOnScrollUp = false,
  resetOnNavigate = true,
  hashOffset = 96,
}) {
  const { pathname, hash, key } = useLocation();
  const [show, setShow] = useState(false);
  const btnRef = useRef(null);
  const last = useRef({ key: null, path: null, same: false });

  // on every navigation: go to the #section if the link has one, otherwise start the page at the top
  useEffect(() => {
    if (last.current.key !== key)
      last.current = {
        key,
        path: pathname,
        same: last.current.path === pathname,
      }; // same = only the #hash changed
    if (hash) return scrollToHash(hash, last.current.same, hashOffset); // same page: glide there; new page: jump there
    if (resetOnNavigate) scrollToY(0, false);
  }, [key, pathname, hash, resetOnNavigate, hashOffset]);

  // show / hide, and the reading-progress ring
  useEffect(() => {
    let raf = 0;
    let lastY = window.scrollY;
    let up = false;

    const update = () => {
      raf = 0;
      const y = window.scrollY;
      if (y < lastY - 4) up = true;
      else if (y > lastY + 4) up = false;
      lastY = y;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      btnRef.current?.style.setProperty(
        "--p",
        max > 0 ? clamp(y / max, 0, 1).toFixed(4) : "0",
      );
      setShow(y > threshold && (!onlyOnScrollUp || up));
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname, threshold, onlyOnScrollUp]);

  const toTop = () => {
    if (!reducedMotion())
      return window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" }); // "instant" also beats a site-wide scroll-behavior: smooth
    } catch (e) {
      window.scrollTo(0, 0);
    }
  };

  return (
    <button
      ref={btnRef}
      type="button"
      className={`gt9-stt${show ? " is-on" : ""}`}
      aria-label="Scroll to top"
      title="Back to top"
      tabIndex={show ? 0 : -1}
      onClick={toTop}
    >
      <svg className="gt9-stt__ring" viewBox="0 0 52 52" aria-hidden="true">
        <circle className="gt9-stt__track" cx="26" cy="26" r="23" />
        <circle
          className="gt9-stt__bar"
          cx="26"
          cy="26"
          r="23"
          pathLength="100"
        />
      </svg>
      <svg
        className="gt9-stt__arrow"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 19V5M5.5 11.5L12 5l6.5 6.5" />
      </svg>
    </button>
  );
}
